const toPermissionList = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => String(item || "").trim())
      .filter(Boolean);
  }
  const single = String(value || "").trim();
  return single ? [single] : [];
};

const PERMISSION_ALIASES = {
  view_product: "view_products",
  manage_product: "manage_products",
};

const PERMISSION_COMPATIBILITY_ALIASES = {
  view_products: ["view_product"],
  manage_products: ["manage_product"],
  view_sales: ["manage_sales", "sale_view_history"],
  sale_view_history: ["manage_sales"],
  sale_create: ["manage_sales"],
  sale_void: ["manage_sales"],
  sale_return: ["manage_sales"],
  sale_discount_override: ["manage_sales"],
  inventory_count_view: ["inventory_count_manage"],
  inventory_selective_start: ["inventory_selective_create"],
  inventory_selective_create: ["inventory_selective_start"],
  inventory_selective_count: ["inventory_selective_capture", "inventory_selective_submit"],
  inventory_selective_capture: ["inventory_selective_count"],
  inventory_selective_submit: ["inventory_selective_count"],
  inventory_selective_cross: ["inventory_selective_cross_manage"],
  inventory_selective_cross_manage: ["inventory_selective_cross"],
  inventory_selective_apply: ["inventory_selective_confirm"],
  inventory_selective_confirm: ["inventory_selective_apply"],
};

export const DEFAULT_SALES_CONFIG = Object.freeze({
  allow_cashier_queue: true,
  allow_billing: false,
  allow_deferred_billing: false,
});

export const SELECTIVE_INVENTORY_PERMISSIONS = Object.freeze([
  "inventory_selective_view",
  "inventory_selective_start",
  "inventory_selective_count",
  "inventory_selective_review",
  "inventory_selective_adjust",
  "inventory_selective_cross",
  "inventory_selective_apply",
  "inventory_selective_cancel",
]);

export const GENERAL_INVENTORY_PERMISSIONS = Object.freeze([
  "inventory_general_view",
  "inventory_general_start",
  "inventory_general_count",
  "inventory_general_review",
  "inventory_general_adjust",
  "inventory_general_cross",
  "inventory_general_apply",
  "inventory_general_cancel",
]);

export const SALES_RETURN_PERMISSIONS = Object.freeze([
  "sales_return_view",
  "sales_return_create",
  "sales_return_approve",
]);

export const normalizeSalesConfig = (data = {}, branchId = null) => ({
  branch_id: data?.branch_id || branchId || null,
  allow_cashier_queue: data?.allow_cashier_queue !== false,
  allow_billing: data?.allow_billing === true,
  allow_deferred_billing: data?.allow_billing === true && data?.allow_deferred_billing === true,
});

const canonicalPermissionCode = (code) => {
  const rawCode = String(code || "").trim();
  return PERMISSION_ALIASES[rawCode] || rawCode;
};

const acceptedPermissionCodes = (code) => {
  const rawCode = String(code || "").trim();
  if (!rawCode) return [];

  const canonicalCode = canonicalPermissionCode(rawCode);
  return Array.from(
    new Set([
      rawCode,
      canonicalCode,
      ...(PERMISSION_COMPATIBILITY_ALIASES[canonicalCode] || []),
      ...Object.entries(PERMISSION_ALIASES)
        .filter(([, targetCode]) => targetCode === canonicalCode)
        .map(([aliasCode]) => aliasCode),
    ].filter(Boolean))
  );
};

export const hasPermission = (permissions, permissionCode, isSuperuser = false) => {
  if (isSuperuser) return true;
  const code = String(permissionCode || "").trim();
  if (!code) return false;
  const owned = new Set(toPermissionList(permissions));
  return acceptedPermissionCodes(code).some((acceptedCode) => owned.has(acceptedCode));
};

export const hasAnyPermission = (permissions, permissionCodes, isSuperuser = false) => {
  if (isSuperuser) return true;
  const required = toPermissionList(permissionCodes);
  if (required.length === 0) return true;
  const owned = new Set(toPermissionList(permissions));
  return required.some((code) =>
    acceptedPermissionCodes(code).some((acceptedCode) => owned.has(acceptedCode))
  );
};

export const hasAllPermissions = (permissions, permissionCodes, isSuperuser = false) => {
  if (isSuperuser) return true;
  const required = toPermissionList(permissionCodes);
  if (required.length === 0) return true;
  const owned = new Set(toPermissionList(permissions));
  return required.every((code) =>
    acceptedPermissionCodes(code).some((acceptedCode) => owned.has(acceptedCode))
  );
};

export const hasAnySelectivePermission = (permissions, isSuperuser = false) =>
  hasAnyPermission(permissions, SELECTIVE_INVENTORY_PERMISSIONS, isSuperuser);

export const hasAnyGeneralInventoryPermission = (permissions, isSuperuser = false) =>
  hasAnyPermission(permissions, GENERAL_INVENTORY_PERMISSIONS, isSuperuser);

export const canAccessPermissionEntry = (
  permissions,
  entry,
  isSuperuser = false,
  salesConfig = DEFAULT_SALES_CONFIG
) => {
  const moduleConfig = salesConfig || DEFAULT_SALES_CONFIG;
  if (entry?.requiresCashierQueue && moduleConfig?.allow_cashier_queue === false) {
    return false;
  }
  if (entry?.requiresBilling && moduleConfig?.allow_billing !== true) {
    return false;
  }
  if (
    entry?.requiresDeferredBilling &&
    (moduleConfig?.allow_billing !== true || moduleConfig?.allow_deferred_billing !== true)
  ) {
    return false;
  }
  if (entry?.superuserOnly || entry?.requiredSuperuser) return Boolean(isSuperuser);
  if (isSuperuser) return true;
  const requiredAll = entry?.requiredPerms || entry?.perms || entry?.permissions || null;
  const requiredAny = entry?.requiredAnyPerms || entry?.anyPerms || entry?.permissionsAny || null;
  return (
    hasAllPermissions(permissions, requiredAll, false) &&
    hasAnyPermission(permissions, requiredAny, false)
  );
};
